import axios from "./axiosInstance";
import config from "./config";
import { captureApiError } from "../utils/sentry";
import { localTodayYmd } from "@mediatechgroup/calendar-date";

const BASE_URL = config.BASE_URL;

// Create a new event
export const createEvent = async (token, eventData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/create`,
      eventData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

// Get all events
export const getEvents = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/events`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEvents", { component: "eventsAPI" });
    throw error;
  }
};

// Get event by ID
export const getEventById = async (token, eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/${eventId}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventById", { component: "eventsAPI" });
    throw error;
  }
};

// Update event
export const updateEvent = async (token, eventId, eventData) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}`,
      eventData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "updateEvent", { component: "eventsAPI" });
    throw error;
  }
};

// Delete event
export const deleteEvent = async (token, eventId) => {
  try {
    const response = await axios.delete(`${BASE_URL}/events/${eventId}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "deleteEvent", { component: "eventsAPI" });
    throw error;
  }
};

// Duplicate event
export const duplicateEvent = async (token, eventId, options = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/duplicate`,
      options,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "duplicateEvent", { component: "eventsAPI" });
    throw error;
  }
};

// Archive event
export const archiveEvent = async (token, eventId) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}/archive`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "archiveEvent", { component: "eventsAPI" });
    throw error;
  }
};

// Unarchive event
export const unarchiveEvent = async (token, eventId, newStatus = "active") => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}/unarchive`,
      { new_status: newStatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "unarchiveEvent", { component: "eventsAPI" });
    throw error;
  }
};

// Bulk archive events
export const bulkArchiveEvents = async (token, eventIds) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/bulk-archive`,
      { event_ids: eventIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "bulkArchiveEvents", { component: "eventsAPI" });
    throw error;
  }
};

// Bulk delete events
export const bulkDeleteEvents = async (token, eventIds) => {
  try {
    const response = await axios.delete(`${BASE_URL}/events/bulk-delete`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",      },
      data: { event_ids: eventIds },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "bulkDeleteEvents", { component: "eventsAPI" });
    throw error;
  }
};

// Get event registrations
export const getEventRegistrations = async (token, eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/${eventId}/registrations`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventRegistrations", { component: "eventsAPI" });
    throw error;
  }
};

// Export event registrations to CSV
export const exportEventRegistrations = async (token, eventId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.format) params.append("format", options.format);
    if (options.status) params.append("status", options.status);

    const url = `${BASE_URL}/events/${eventId}/registrations/export${params.toString() ? `?${params}` : ""}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,      },
      responseType: options.format === "csv" ? "blob" : "json",
    });

    // If CSV, trigger download
    if (options.format === "csv") {
      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `registrations_${eventId}_${localTodayYmd()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return { success: true };
    }

    return response.data;
  } catch (error) {
    captureApiError(error, "exportEventRegistrations", { component: "eventsAPI" });
    throw error;
  }
};

// Register for an event
export const registerForEvent = async (token, eventId, registrationData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/register`,
      registrationData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "registerForEvent", { component: "eventsAPI" });
    throw error;
  }
};

// Get current user's event registrations
export const getMyRegistrations = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/my-registrations`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getMyRegistrations", { component: "eventsAPI" });
    throw error;
  }
};

// Cancel a registration
export const cancelRegistration = async (token, eventId, registrationId, options = {}) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
        data: {
          void_invoice: !!options.voidInvoice,
          refund_payment: !!options.refundPayment,
          // Staff: refund what the event's refund policy gives back. A
          // family's own cancel always gets the policy, chosen by the server.
          apply_policy: !!options.applyPolicy,
        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "cancelRegistration", { component: "eventsAPI" });
    throw error;
  }
};

// Read-only preview of the billing impact of cancelling an event registration.
export const getEventCancellationPreview = async (token, eventId, registrationId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/cancellation-preview`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventCancellationPreview", { component: "eventsAPI" });
    throw error;
  }
};

// Admin register a member for an event
export const adminRegisterMember = async (token, eventId, registrationData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/admin/register`,
      registrationData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "adminRegisterMember", { component: "eventsAPI" });
    throw error;
  }
};

// Invite a non-member to an event via email
export const inviteNonMember = async (token, eventId, inviteData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/invite`,
      inviteData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "inviteNonMember", { component: "eventsAPI" });
    throw error;
  }
};

// Toggle waitlist for an event
export const toggleWaitlist = async (token, eventId, enabled) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}`,
      { waitlist_enabled: enabled },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "toggleWaitlist", { component: "eventsAPI" });
    throw error;
  }
};

// Get waitlist entries for an event
export const getEventWaitlist = async (token, eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/${eventId}/waitlist`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventWaitlist", { component: "eventsAPI" });
    throw error;
  }
};

// Manually promote a registration from waitlist
export const promoteFromWaitlist = async (token, eventId, registrationId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/waitlist/${registrationId}/promote`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "promoteFromWaitlist", { component: "eventsAPI" });
    throw error;
  }
};

// Remove a registration from waitlist
export const removeFromWaitlist = async (token, eventId, registrationId, reason = "") => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/events/${eventId}/waitlist/${registrationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
        data: { reason },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "removeFromWaitlist", { component: "eventsAPI" });
    throw error;
  }
};

// Reorder waitlist positions
export const reorderWaitlist = async (token, eventId, orderedIds) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}/waitlist/reorder`,
      { orderedIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "reorderWaitlist", { component: "eventsAPI" });
    throw error;
  }
};

// Update reminder settings for an event
export const updateReminderSettings = async (token, eventId, reminderSettings) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}/reminders`,
      { reminder_settings: reminderSettings },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "updateReminderSettings", { component: "eventsAPI" });
    throw error;
  }
};

// Get all event categories
export const getEventCategories = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/categories`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventCategories", { component: "eventsAPI" });
    throw error;
  }
};

// Create a new event category
export const createEventCategory = async (token, categoryData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/categories`,
      categoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "createEventCategory", { component: "eventsAPI" });
    throw error;
  }
};

// Update an event category
export const updateEventCategory = async (token, categoryId, categoryData) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/categories/${categoryId}`,
      categoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "updateEventCategory", { component: "eventsAPI" });
    throw error;
  }
};

// Delete an event category
export const deleteEventCategory = async (token, categoryId) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/events/categories/${categoryId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "deleteEventCategory", { component: "eventsAPI" });
    throw error;
  }
};

// Get event attendance summary
export const getEventAttendance = async (token, eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/${eventId}/attendance`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventAttendance", { component: "eventsAPI" });
    throw error;
  }
};

// Check in a registration
export const checkInRegistration = async (token, eventId, registrationId, notes = "") => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/check-in`,
      { notes },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "checkInRegistration", { component: "eventsAPI" });
    throw error;
  }
};

// Check out a registration
export const checkOutRegistration = async (token, eventId, registrationId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/check-out`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "checkOutRegistration", { component: "eventsAPI" });
    throw error;
  }
};

// Undo check-in for a registration
export const undoCheckIn = async (token, eventId, registrationId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/undo-check-in`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "undoCheckIn", { component: "eventsAPI" });
    throw error;
  }
};

// Mark a registration as no-show
export const markNoShow = async (token, eventId, registrationId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/mark-no-show`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "markNoShow", { component: "eventsAPI" });
    throw error;
  }
};

// Get event attachments
export const getEventAttachments = async (token, eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/${eventId}/attachments`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventAttachments", { component: "eventsAPI" });
    throw error;
  }
};

// Upload event attachment
export const uploadEventAttachment = async (token, eventId, attachmentData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/attachments`,
      attachmentData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "uploadEventAttachment", { component: "eventsAPI" });
    throw error;
  }
};

// Delete event attachment
export const deleteEventAttachment = async (token, eventId, attachmentId) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/events/${eventId}/attachments/${attachmentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "deleteEventAttachment", { component: "eventsAPI" });
    throw error;
  }
};

// Set cover image for event
export const setCoverImage = async (token, eventId, attachmentId) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}/attachments/${attachmentId}/cover`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "setCoverImage", { component: "eventsAPI" });
    throw error;
  }
};

// Get all event templates
export const getEventTemplates = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/templates`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventTemplates", { component: "eventsAPI" });
    throw error;
  }
};

// Get event template by ID
export const getEventTemplateById = async (token, templateId) => {
  try {
    const response = await axios.get(`${BASE_URL}/events/templates/${templateId}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventTemplateById", { component: "eventsAPI" });
    throw error;
  }
};

// Create event template
export const createEventTemplate = async (token, templateData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/templates`,
      templateData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "createEventTemplate", { component: "eventsAPI" });
    throw error;
  }
};

// Update event template
export const updateEventTemplate = async (token, templateId, templateData) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/templates/${templateId}`,
      templateData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "updateEventTemplate", { component: "eventsAPI" });
    throw error;
  }
};

// Delete event template
export const deleteEventTemplate = async (token, templateId) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/events/templates/${templateId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "deleteEventTemplate", { component: "eventsAPI" });
    throw error;
  }
};

// Save event as template
export const saveEventAsTemplate = async (token, eventId, templateData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/save-as-template`,
      templateData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "saveEventAsTemplate", { component: "eventsAPI" });
    throw error;
  }
};

// Create event from template
export const createEventFromTemplate = async (token, templateId, eventData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/from-template/${templateId}`,
      eventData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "createEventFromTemplate", { component: "eventsAPI" });
    throw error;
  }
};

// Get event analytics
export const getEventAnalytics = async (token, startDate, endDate) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const response = await axios.get(`${BASE_URL}/events/analytics?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventAnalytics", { component: "eventsAPI" });
    throw error;
  }
};

// Get QR code for a registration
export const getRegistrationQRCode = async (token, eventId, registrationId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/qr-code`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "getRegistrationQRCode", { component: "eventsAPI" });
    throw error;
  }
};

// Validate QR code and check in/out. `eventId` (optional) pins the scan to
// the event whose attendance page is scanning: a code for another event is
// refused instead of checked into that event (Phase D2).
/** @param {string | null} [eventId] */
export const validateQRCode = async (token, qrCode, action = "check_in", eventId = null) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/validate-qr`,
      { qr_code: qrCode, action, ...(eventId ? { event_id: eventId } : {}) },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "validateQRCode", { component: "eventsAPI" });
    throw error;
  }
};

// Regenerate QR code for a registration
export const regenerateQRCode = async (token, eventId, registrationId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/regenerate-qr`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "regenerateQRCode", { component: "eventsAPI" });
    throw error;
  }
};

// Get calendar export links for an event
export const getCalendarLinks = async (token, eventId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/events/${eventId}/calendar-links`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "getCalendarLinks", { component: "eventsAPI" });
    throw error;
  }
};

// Download ICS file for an event
export const downloadEventICS = async (token, eventId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/events/${eventId}/export-ics`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "downloadEventICS", { component: "eventsAPI" });
    throw error;
  }
};

// ===== Event Feedback API =====

// Get feedback for an event
export const getEventFeedback = async (token, eventId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.status) params.append("status", options.status);
    if (options.includePrivate) params.append("includePrivate", "true");

    const response = await axios.get(
      `${BASE_URL}/events/${eventId}/feedback?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventFeedback", { component: "eventsAPI" });
    throw error;
  }
};

// Get current user's feedback for an event
export const getMyEventFeedback = async (token, eventId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/events/${eventId}/my-feedback`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "getMyEventFeedback", { component: "eventsAPI" });
    throw error;
  }
};

// Submit feedback for an event
export const submitEventFeedback = async (token, eventId, feedbackData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/feedback`,
      feedbackData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "submitEventFeedback", { component: "eventsAPI" });
    throw error;
  }
};

// Update feedback (member can update pending, admin can moderate)
export const updateEventFeedback = async (token, eventId, feedbackId, updates) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/events/${eventId}/feedback/${feedbackId}`,
      updates,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "updateEventFeedback", { component: "eventsAPI" });
    throw error;
  }
};

// Delete feedback (admin only)
export const deleteEventFeedback = async (token, eventId, feedbackId) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/events/${eventId}/feedback/${feedbackId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "deleteEventFeedback", { component: "eventsAPI" });
    throw error;
  }
};

// ===== Event Announcements API =====

// Get announcements for an event
export const getEventAnnouncements = async (token, eventId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/events/${eventId}/announcements`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "getEventAnnouncements", { component: "eventsAPI" });
    throw error;
  }
};

// Create and send an announcement to event registrants
export const createEventAnnouncement = async (token, eventId, announcementData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/announcements`,
      announcementData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "createEventAnnouncement", { component: "eventsAPI" });
    throw error;
  }
};

// Delete an announcement
export const deleteEventAnnouncement = async (token, eventId, announcementId) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/events/${eventId}/announcements/${announcementId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "deleteEventAnnouncement", { component: "eventsAPI" });
    throw error;
  }
};

// Send bulk email to event registrations
export const sendBulkRegistrationEmail = async (token, eventId, data) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/registrations/email`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "sendBulkRegistrationEmail", { component: "eventsAPI" });
    throw error;
  }
};

// Record payment for an event registration
export const recordEventRegistrationPayment = async (token, eventId, registrationId, paymentData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/record-payment`,
      paymentData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "recordEventRegistrationPayment", { component: "eventsAPI" });
    throw error;
  }
};

// Admin: update payment status (mark paid, waive)
export const adminUpdateEventPaymentStatus = async (token, eventId, registrationId, paymentStatus, note = null) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/events/${eventId}/registrations/${registrationId}/payment-status`,
      { payment_status: paymentStatus, note },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "adminUpdateEventPaymentStatus", { component: "eventsAPI" });
    throw error;
  }
};

// ===== Public Event API =====

// Get public events list (no authentication required)
export const getPublicEvents = async (tenantId) => {
  try {
    const response = await axios.get(`${BASE_URL}/public/events`, {
      headers: {
        "x-tenant-id": tenantId,
      },
    });
    return response.data;
  } catch (error) {
    captureApiError(error, "getPublicEvents", { component: "eventsAPI" });
    throw error;
  }
};

// Get public event by ID (no authentication required)
export const getPublicEvent = async (tenantId, eventId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/public/events/${eventId}`,
      {
        headers: {
          "x-tenant-id": tenantId,
        },
      }
    );
    return response.data;
  } catch (error) {
    captureApiError(error, "getPublicEvent", { component: "eventsAPI" });
    throw error;
  }
};
